import { useContext, useMemo } from 'react';
import { PermissionContext } from '../contexts/PermissionContext';

// Overrides for menuPermission keys that don't derive from simple singular/plural
// matching against the login menu (e.g. backend typos like "campaingns").
const MODULE_PERMISSION_MAP = {
  campaign: 'campaingns',
  campaigns: 'campaingns',
};

function normalizeKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

// Treats "job"/"jobs", "candidate"/"candidates", etc. as the same key.
function keysMatch(a, b) {
  const left = normalizeKey(a);
  const right = normalizeKey(b);
  if (!left || !right) return false;
  return left === right || `${left}s` === right || `${right}s` === left;
}

function resolveMenuKey(normalized) {
  try {
    const rawMenu = localStorage.getItem('menu');
    if (!rawMenu) return null;
    const menu = JSON.parse(rawMenu);
    if (!Array.isArray(menu)) return null;

    const match = menu.find((item) => {
      const key = normalizeKey(item?.key);
      return key && keysMatch(normalized, key);
    });
    return match ? normalizeKey(match.key) : null;
  } catch {
    return null;
  }
}

function findModulePermission(all, moduleName) {
  if (!all || typeof all !== 'object' || Array.isArray(all) || !moduleName) {
    return { found: false, permissions: {} };
  }

  const normalized = normalizeKey(moduleName);
  const mapped = MODULE_PERMISSION_MAP[normalized] ?? normalized;
  const menuResolved = resolveMenuKey(normalized);
  const candidates = [mapped, menuResolved, normalized].filter(Boolean);
  const keys = Object.keys(all);

  const matchedKey = keys.find((key) => candidates.some((candidate) => normalizeKey(key) === candidate))
    ?? keys.find((key) => candidates.some((candidate) => keysMatch(candidate, key)));

  return {
    found: Boolean(matchedKey),
    permissions: matchedKey ? (all[matchedKey] ?? {}) : {},
  };
}

function readStoredPermissions(moduleName) {
  try {
    const raw = localStorage.getItem('menuPermission');
    if (!raw) return {};
    const all = JSON.parse(raw);
    return findModulePermission(all, moduleName).permissions;
  } catch {
    return {};
  }
}

// Permission values come as 1/0 integers from the login snapshot and booleans
// from /me/permissions. Absent CRUD key keeps the historical default-allow
// behaviour (unconfigured = unrestricted).
function permissionScalar(value) {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return value.value;
  }
  return value;
}

function isAllowed(value) {
  const scalar = permissionScalar(value);
  if (scalar === undefined || scalar === null) return true;
  return scalar === true || scalar === 1 || scalar === '1'
    || String(scalar).trim().toLowerCase() === 'true';
}

function isExplicitlyAllowed(value) {
  const scalar = permissionScalar(value);
  if (scalar === undefined || scalar === null) return false;
  return scalar === true || scalar === 1 || scalar === '1'
    || String(scalar).trim().toLowerCase() === 'true';
}

function permissionValue(permissions, key) {
  if (!permissions || typeof permissions !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(permissions, key)) return permissions[key];
  const wanted = normalizeKey(key);
  const matchedKey = Object.keys(permissions).find(
    (candidate) => normalizeKey(candidate) === wanted,
  );
  return matchedKey ? permissions[matchedKey] : undefined;
}

export function useMenuPermission(moduleName) {
  const { permissions: livePermissions, loading } = useContext(PermissionContext);

  return useMemo(() => {
    const fullAccess = Boolean(livePermissions?.['*']?.['*']);
    const live = findModulePermission(livePermissions, moduleName);

    // Once the live module is available it is authoritative. During initial
    // load (or if an older deployment has no /me/permissions entry for this
    // module), fall back to the login snapshot for compatibility.
    const p = !loading && live.found ? live.permissions : readStoredPermissions(moduleName);

    if (fullAccess) {
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true,
        canApprove: true,
        canReject: true,
        canReApprove: true,
        canSendApprove: true,
      };
    }

    return {
      canView:        isAllowed(permissionValue(p, 'view')),
      canAdd:         isAllowed(permissionValue(p, 'add')),
      canEdit:        isAllowed(permissionValue(p, 'edit')),
      canDelete:      isAllowed(permissionValue(p, 'delete')),
      canApprove:     isAllowed(permissionValue(p, 'approve')),
      canReject:      isAllowed(permissionValue(p, 'reject')),
      canReApprove:   isExplicitlyAllowed(permissionValue(p, 'reApprove')),
      canSendApprove: isExplicitlyAllowed(permissionValue(p, 'sendApprove')),
    };
  }, [moduleName, livePermissions, loading]);
}
